/**
 * openai-compat-stream.ts
 * 兼容路径（OpenAI 协议）手写 SSE 执行器 + 手动工具循环。
 * 与原生 executeAnthropicStream 对称：手搓请求、复用注入的 fetch（生产传 errorCheckingFetch）、
 * 手动解析 SSE、复刻终止语义。不依赖 Pinia store，可离线单测。
 * tool_call 增量累积委托给纯函数 reducer（./openai-stream-tools）。
 */
import type { ToolLifecycle } from '../tools/tool-lifecycle'
import type { StreamEvent } from './llm'
import type { ParsedToolCall } from './openai-stream-tools'

import { duplicateToolResult, getToolLifecycleByName, ignoredAfterTerminalResult, normalizeToolResult, ToolExecutionLedger, ToolInputError } from '../tools/tool-lifecycle'
import {
  createOpenAIStreamState,
  finalizeToolCalls,
  isToolCallsFinish,
  reduceOpenAIChunk,
} from './openai-stream-tools'
import { getDisallowedToolCallResult, shouldUsePlainTextFallback } from './structured-output'

export interface CompatStreamResult {
  textContent: string
  reasoningContent: string
  toolCalls: ParsedToolCall[]
  finishReason: string
}

export async function executeOpenAICompatStream(
  url: string,
  body: Record<string, unknown>,
  headers: Record<string, string>,
  onEvent: (e: StreamEvent) => void | Promise<void>,
  fetchFn: typeof globalThis.fetch,
  abortSignal?: AbortSignal,
): Promise<CompatStreamResult> {
  const response = await fetchFn(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    ...(abortSignal ? { signal: abortSignal } : {}),
  })

  // errorCheckingFetch 对不可重试错误返回空 200 流；无 body 时安静结束
  if (!response.body) {
    const s = createOpenAIStreamState()
    return { textContent: '', reasoningContent: '', toolCalls: finalizeToolCalls(s), finishReason: s.finishReason }
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const state = createOpenAIStreamState()
  const startedTools = new Set<number>() // 已发过 streaming-start 的 tool index
  let prevTextLen = 0
  let prevReasoningLen = 0
  let sseBuffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done)
      break
    sseBuffer += decoder.decode(value, { stream: true })
    const lines = sseBuffer.split('\n')
    sseBuffer = lines.pop() ?? ''

    for (const rawLine of lines) {
      const line = rawLine.trim()
      if (!line.startsWith('data: ') || line === 'data: [DONE]')
        continue
      let parsed: unknown
      try {
        parsed = JSON.parse(line.slice(6))
      }
      catch {
        continue
      }
      reduceOpenAIChunk(state, parsed)

      // 新增文本立即发 text-delta
      if (state.textContent.length > prevTextLen) {
        await onEvent({ type: 'text-delta', text: state.textContent.slice(prevTextLen) })
        prevTextLen = state.textContent.length
      }
      // 新增思考内容立即发 reasoning-delta（DeepSeek / GPT-luna 等通过 compat 返回 reasoning_content）
      if (state.reasoningContent.length > prevReasoningLen) {
        await onEvent({ type: 'reasoning-delta', text: state.reasoningContent.slice(prevReasoningLen) })
        prevReasoningLen = state.reasoningContent.length
      }
      // 每个 tool index 首次 id+name 齐备发一次 streaming-start
      for (const tc of state.toolCalls.values()) {
        if (tc.id && tc.name && !startedTools.has(tc.index)) {
          startedTools.add(tc.index)
          await onEvent({ type: 'tool-call-streaming-start', toolCallId: tc.id, toolName: tc.name })
        }
      }
    }
  }

  return {
    textContent: state.textContent,
    reasoningContent: state.reasoningContent,
    toolCalls: finalizeToolCalls(state),
    finishReason: state.finishReason,
  }
}

export interface CompatLoopDeps {
  streamOnce: (messages: unknown[]) => Promise<CompatStreamResult>
  toolExecutors: Map<string, (input: unknown, opts: unknown) => Promise<unknown>>
  terminalToolNames: Set<string>
  maxSteps: number
  onEvent: (e: StreamEvent) => void | Promise<void>
  ledger?: ToolExecutionLedger
  onToolBudgetExceeded?: () => void
  actionToolNames?: ReadonlySet<string>
  lifecycleByName?: ReadonlyMap<string, ToolLifecycle>
  requireToolCall?: boolean
  allowedToolNames?: ReadonlySet<string>
}

export async function runCompatToolLoop(initialMessages: unknown[], deps: CompatLoopDeps): Promise<void> {
  const { streamOnce, toolExecutors, terminalToolNames, maxSteps, onEvent } = deps
  let messages = [...initialMessages]
  const ledger = deps.ledger ?? new ToolExecutionLedger(maxSteps)

  for (let step = 0; step < maxSteps; step++) {
    const result = await streamOnce(messages)
    if (deps.requireToolCall && result.textContent.trim())
      await onEvent({ type: 'text-delta', text: result.textContent })

    // 非工具结束：正常收尾
    if (!isToolCallsFinish(result.finishReason) || result.toolCalls.length === 0) {
      await onEvent({ type: 'finish', finishReason: result.finishReason === 'stop' ? 'stop' : result.finishReason } as StreamEvent)
      return
    }

    if (shouldUsePlainTextFallback(
      result.toolCalls.map(tool => tool.name),
      result.textContent,
      deps.requireToolCall,
      deps.allowedToolNames,
    )) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    // OpenAI 格式 assistant 消息：带 tool_calls 数组
    const assistantMsg = {
      role: 'assistant',
      content: deps.requireToolCall ? '' : result.textContent || '',
      tool_calls: result.toolCalls.map(tc => ({
        id: tc.id,
        type: 'function',
        function: { name: tc.name, arguments: tc.rawArguments ?? JSON.stringify(tc.input ?? {}) },
      })),
    }

    // 按模型返回顺序执行；遇到首个终止工具后忽略同批后续调用。
    const toolMsgs: Array<Record<string, unknown>> = []
    let hitTerminal = false
    let hitBudget = false
    for (const tc of result.toolCalls) {
      if (hitTerminal) {
        await onEvent({ type: 'tool-result', toolCallId: tc.id, result: ignoredAfterTerminalResult() })
        continue
      }
      if (deps.allowedToolNames && !deps.allowedToolNames.has(tc.name)) {
        toolMsgs.push({ role: 'tool', tool_call_id: tc.id, content: getDisallowedToolCallResult(tc.name, deps.allowedToolNames) })
        continue
      }
      const lifecycle = deps.actionToolNames?.has(tc.name) ? 'action' : getToolLifecycleByName(tc.name, deps.lifecycleByName, terminalToolNames)
      const claim = ledger.claim(tc.id, tc.name, tc.input)
      if (!claim.accepted) {
        const content = duplicateToolResult(claim)
        await onEvent({ type: 'tool-result', toolCallId: tc.id, result: content })
        if (claim.reason === 'budget') {
          ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
          hitBudget = true
          break
        }
        toolMsgs.push({ role: 'tool', tool_call_id: tc.id, content })
        continue
      }
      const executor = toolExecutors.get(tc.name)
      let content = ''
      let executionError: unknown
      if (tc.inputError) {
        executionError = new ToolInputError(tc.inputError)
        content = tc.inputError
      }
      else if (executor) {
        try {
          const r = await executor(tc.input, {})
          content = normalizeToolResult(r, tc.name, lifecycle)
        }
        catch (e) {
          executionError = e
          content = `Error: ${e}`
        }
      }
      else {
        if (lifecycle === 'terminal')
          throw new Error(`Terminal tool "${tc.name}" is not registered`)
        content = `Tool "${tc.name}" not found`
      }
      await onEvent({ type: 'tool-result', toolCallId: tc.id, result: content })

      if (lifecycle === 'terminal') {
        if (executionError && !ledger.allowInputCorrection(executionError, tc.id, tc.name, tc.input)) {
          if (executionError instanceof ToolInputError) {
            await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
            return
          }
          throw executionError
        }
        if (!executionError) {
          hitTerminal = true
          continue
        }
      }
      if (ledger.budgetReached) {
        ledger.notifyBudgetOnce(deps.onToolBudgetExceeded)
        hitBudget = true
        break
      }
      toolMsgs.push({ role: 'tool', tool_call_id: tc.id, content })
    }

    if (hitTerminal || hitBudget || toolMsgs.length === 0) {
      await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
      return
    }

    messages = [...messages, assistantMsg, ...toolMsgs]
  }

  await onEvent({ type: 'finish', finishReason: 'stop' } as StreamEvent)
}
